import {
  Box,
  Button,
  Dialog,
  Backdrop,
  TextField,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { UsedBank } from 'library/types';
import apiService from 'library/api-service';
import { FormattedMessage } from 'react-intl';
import { GridRowId } from '@mui/x-data-grid-premium';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';

type UpdateUsedBankDialogProps = {
  open: boolean;
  usedBankId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const UpdateUsedBankDialog = ({ open, usedBankId, handleClose: closeDialog, refresh }: UpdateUsedBankDialogProps) => {
  // other hooks
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [title, setTitle] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  // async helper functions
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!title) {
      setError(true);
      return;
    }

    const postData: UsedBank = {
      id: usedBankId as number,
      title,
    };

    try {
      setLoading(true);

      const response = await apiService.updateUsedBank(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="usedBanks.updateUsedBankCompleted"
            defaultMessage="Banka düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getUsedBankInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificUsedBank(usedBankId as number);

      if (response.isSuccess) {
        setTitle(response.data.title);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [usedBankId]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setTitle('');
    setError(false);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTitle(event.target.value);

  // useEffect hooks
  useEffect(() => {
    if (title) {
      setError(false);
    }
  }, [title]);

  useEffect(() => {
    if (open && usedBankId) {
      getUsedBankInfos();
    }
  }, [open, usedBankId, getUsedBankInfos]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="usedBanks.updateUsedBank" defaultMessage="Banka Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="usedBanks.updateUsedBankText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili banka için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <TextField
            label={<FormattedMessage id="usedBanks.title" defaultMessage="Adı" />}
            fullWidth
            size="small"
            onChange={onChangeHandler}
            value={title}
            error={error}
            helperText={
              error && <FormattedMessage id="validations.required" defaultMessage="Bu alan boş bırakılamaz" />
            }
          />
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default UpdateUsedBankDialog;
